import axios from 'axios'
import _ from 'lodash'

function createImageUrl (imageName, contentType) {
  const urlStr = `${process.env.VUE_APP_API_URI}/forum/images?name=${imageName}&contentType=${contentType}`

  return encodeURI(urlStr)
}

/**
 * Cleans "old" embedded image data to allow images to be rendered even when method
 * of displaying images has changed. Old method was to use image file name as placeholder, now
 * we will be using direct link to server endpoint
 * @param {Array} contentArr
 * @param {String} contentType - "post" or "reply"
 */
function cleanEmbeddedImagesWithinContent (contentArr, contentType) {
  contentArr.forEach(content => {
    if (content.type === 'image') {
      const srcKeyword = content.attrs.src.split(':').shift()

      if (!['data', 'http', 'https'].includes(srcKeyword)) {
        content.attrs.src = createImageUrl(content.attrs.src, contentType)
      }
    } else if (content.content && content.content instanceof Array) {
      cleanEmbeddedImagesWithinContent(content.content, contentType)
    }
  })
}

/**
 * Cleans the embedded images for a post to allow it to render images
 * @param {Object} post
 * @return {Object}
 */
export function cleanEmbeddedImagesWithinPost (postContent) {
  const postContentCopy = _.cloneDeep(postContent)

  cleanEmbeddedImagesWithinContent(postContentCopy.content, 'post')

  return postContentCopy
}

/**
 * Cleans the embedded images for a reply to allow it to render images
 * @param {Object} replyContent
 * @return {Object}
 */
export function cleanEmbeddedImagesWithinReply (replyContent) {
  const replyContentCopy = _.cloneDeep(replyContent)

  cleanEmbeddedImagesWithinContent(replyContentCopy.content, 'reply')

  return replyContentCopy
}

/**
 * TODO: Modify this function to better handle all file types
 * Attach the files into the 'Posts' or 'Replies' folder.
 * @param {Array} files
 * @param {Number} contentId
 * @param {Boolean} isPost
 */
export async function attachFiles (files, contentId, isPost = false) {
  if (!files.length) {
    return
  }

  // Create a form data to send the images to the server.
  const formData = new FormData()

  // Add every file into the form data.
  files.forEach(file => {
    formData.append('files', file)
  })

  // Define the Body params: contentType and contentId.
  // NOTE: We are specifying the files belong to a post and sending the post id to create its folder in S3.
  const contentType = isPost ? 'posts' : 'replies'
  formData.append('contentType', contentType)
  formData.append('contentId', contentId)

  // Send the files to the server.
  await axios.post(`${process.env.VUE_APP_API_URI}/forum/files`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  )
}

/**
 * TODO: Modfiy this function to better handle all file types
 * Fetches the attached files from 'Posts' or 'Replies' folder.
 * @param {String} contentType
 * @param {Number} contentId
 */
export async function getAttachedFiles (contentType, contentId) {
  const files = await axios.get(`${process.env.VUE_APP_API_URI}/forum/files`, {
    params: {
      contentType,
      contentId
    }
  })

  return files.data.map(file => {
    return {
      isImage: true,
      src: `data:image/*;base64,${file}`
    }
  })
}

/**
 * Saves the embedded images of a post/reply to S3 so that it can be used
 * whne loading up the saved post/reply
 * @param {Array} images - the array of image objects that contains the contents of the images
 * @param {*} contentType - The content type in which the images are saved for. It is either "post" or "reply"
 * @return {Promise} - resolves into an array of strings that represent the hashed file names saved on s3
 */
async function saveEmbeddedImages (images, contentType) {
  try {
    const formData = new FormData()

    images.forEach(image => {
      formData.append('files', image)
    })

    formData.append('contentType', contentType)

    const savedImageFileNames = (await axios.post(`${process.env.VUE_APP_API_URI}/forum/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )).data

    return savedImageFileNames
  } catch (error) {
    console.error(`Error while saving embedded images: ${error}`)
    throw error
  }
}

/**
 * Recursively searches for the content that represents the image
 * by matching the src string
 * @param {Array} contentArr - The array of contents of the post / reply
 * @param {*} src - src string used to find image
 * @return {Object}
 */
function findImageWithinContent (contentArr, src) {
  let contentInfo = null

  for (let i = 0; i < contentArr.length; i++) {
    const content = contentArr[i]

    if (content.type === 'image' && content.attrs.src === src) {
      contentInfo = content
    } else if (content.content && content.content instanceof Array) {
      contentInfo = findImageWithinContent(content.content, src)
    }

    if (contentInfo) {
      break
    }
  }

  return contentInfo
}

/**
 * Replaces the image attributes within the editor content with its new file name. Returns a new content obj
 * @param {Array} content - Array JSON objects representing the content of the editor
 * @param {Array} images - Array of image objects that are used to find and replace the image contents
 * @return {Array}
 */
function replaceImagesWithinContent (content, images) {
  const contentCopy = _.cloneDeep(content)

  images.forEach(image => {
    const contentImage = findImageWithinContent(contentCopy, image.src)

    if (contentImage) {
      contentImage.attrs.src = image.newSrc
      contentImage.attrs.title = image.name
      contentImage.attrs.alt = image.name
    }
  })

  return contentCopy
}

/**
 * Saves the embedded images onto S3 and replaces the embedded images within a new content obj
 * with the saved file names to make the content ready to be saved into the db
 * @param {Array} content - array of content objs
 * @param {*} embeddedImages - Array of embedded images
 * @param {*} contentType - The content type being saved: 'post' or 'reply'
 * @return {Array}
 */
async function saveAndReplaceEmbeddedImages (content, embeddedImages, contentType) {
  const savedImageFileNames = await saveEmbeddedImages(embeddedImages, contentType)
  const savedEmbeddedImages = savedImageFileNames.map((fileName, index) => {
    const embeddedImage = embeddedImages[index]

    return {
      name: fileName,
      src: embeddedImage.src,
      newSrc: createImageUrl(fileName, contentType)
    }
  })

  return replaceImagesWithinContent(content, savedEmbeddedImages)
}

/**
 * Saves the embedded images for a post and replaces all of the image src strings with
 * the newly created src strings that the post will use to render the saved images.
 * Returns a new content obj, does not modify input.
 * @param {Array} content - array of content objs
 * @param {Array} embeddedImages - array of image objs
 * @return {Array}
 */
export function saveAndReplaceImagesWithinPost (content, embeddedImages) {
  return saveAndReplaceEmbeddedImages(content, embeddedImages, 'post')
}

/**
 * Saves the embedded images for a reply content and replaces all of the image src with
 * the newly created src strings that the reply will use to render the saved images.
 * Creates a new content obj, it does not modify the input
 * @param {Array} content - array of content objs
 * @param {Array} embeddedImages - array of image objs
 * @return {Array}
 */
export function saveAndReplaceImagesWithinReply (content, embeddedImages) {
  return saveAndReplaceEmbeddedImages(content, embeddedImages, 'reply')
}

/**
 * Validate if content is empty by removing all html tags
 * Except self quoted img tag
 * @param {String} HTML HTML version of content
 * @return {Boolean}
 */
export function isContentEmpty (HTML) {
  const regex = /<\/?(?!img)\w*\b[^>]*>/ig
  return HTML.replace(regex, '').trim().length === 0
}
